import { NavLink } from 'react-router-dom';
import { cn } from '@/utils/cn';

interface NavTab {
  readonly label: string;
  readonly to: string;
  /** Reachable without a session. Mirrors the route table: only Contact and FAQ are open. */
  readonly open?: boolean;
}

/** US-004 AC-001 fixes this set: Home, Contact, FAQ and Forms. */
const TABS: readonly NavTab[] = [
  { label: 'Home', to: '/' },
  { label: 'Contact', to: '/contact', open: true },
  { label: 'FAQ', to: '/faq', open: true },
  { label: 'Forms', to: '/forms' },
];

/**
 * Portal tabs. The current tab is marked with `aria-current` as well as with colour and weight, so the
 * distinction survives for a screen reader and for anyone who cannot rely on colour (WCAG 1.4.1).
 *
 * A visitor with no session gets the open tabs only. Offering the guarded ones would put a control on
 * screen whose only outcome is a bounce back to login — the defect that closed Contact and FAQ in the
 * first place — while offering none stranded a visitor who reached Contact from the login header with
 * no way across to FAQ.
 */
export function PortalNav({ signedIn }: { readonly signedIn: boolean }): React.JSX.Element {
  const tabs = signedIn ? TABS : TABS.filter((tab) => tab.open === true);

  return (
    <nav aria-label="Portal sections">
      <ul className="flex flex-wrap gap-2">
        {tabs.map((tab) => (
          <li key={tab.to}>
            <NavLink
              to={tab.to}
              end={tab.to === '/'}
              className={({ isActive }): string =>
                cn(
                  'inline-flex rounded-full px-5 py-2 text-[13.5px] font-semibold transition-colors',
                  isActive
                    ? 'bg-brand-surface text-on-brand'
                    : 'text-brand-600 hover:bg-brand-50 bg-transparent',
                )
              }
            >
              {tab.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
